import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Clock, ChartLine, Storefront, Users, Sparkle, Star, MapPin, ArrowRight } from "@phosphor-icons/react";

const TABS = [
  { k: "home", l: "Home" },
  { k: "today", l: "Today" },
  { k: "calendar", l: "Calendar" },
  { k: "bookings", l: "Bookings" },
  { k: "earnings", l: "Earnings" },
  { k: "performance", l: "Performance" },
];

export default function ProDashboard() {
  const { user } = useAuth();
  const [qp, setQp] = useSearchParams();
  const active = qp.get("tab") || "home";
  const [studio, setStudio] = useState(null);
  const [studioLoaded, setStudioLoaded] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    if (!user) return;
    // Distinguish "loading" from "no studio yet" — a fresh Professional who
    // hasn't launched their Studio must not get stuck on the loading screen.
    api.get("/my/studio")
      .then((r) => setStudio(r.data))
      .catch(() => setStudio(null))
      .finally(() => setStudioLoaded(true));
    api.get("/bookings/mine").then((r) => setBookings(r.data || [])).catch(() => setBookings([]));
    api.get("/earnings").then((r) => setEarnings(r.data)).catch(() => setEarnings(null));
  }, [user]);

  if (!user) return <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="pro-signin">Sign in as a Professional.</div>;
  if (user.role !== "professional" && user.role !== "owner") {
    return (
      <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="pro-become">
        <p className="overline mb-3">Become a Professional</p>
        <h1 className="font-display text-3xl">Ready to open your Studio?</h1>
        <Link to="/pro/welcome" className="btn-gold inline-block mt-6 px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Create My Studio</Link>
      </div>
    );
  }
  if (!studioLoaded) return <div className="max-w-3xl mx-auto px-5 py-10 text-cream/50" data-testid="pro-loading">Loading dashboard…</div>;
  // Fresh Professional — API responded, but no Studio launched yet.
  // Guide them out of the dead-end into the onboarding flow.
  if (!studio) {
    return (
      <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="pro-no-studio">
        <p className="overline mb-3">Almost there</p>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter">Your Studio isn't live yet.</h1>
        <p className="text-cream/70 mt-4 text-sm">Finish creating your Studio to see today's bookings, calendar, earnings and more.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/pro/onboarding" data-testid="cta-finish-studio" className="btn-gold px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">Continue setup</Link>
          <Link to="/pro/welcome" data-testid="cta-welcome" className="btn-ghost px-6 py-3 rounded-full text-xs uppercase tracking-[0.25em]">What's involved?</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 pt-8 pb-24" data-testid="pro-dashboard">
      <p className="overline mb-2">My Studio</p>
      <h1 className="font-display text-3xl md:text-4xl tracking-tighter">Good evening, {user.name.split(" ")[0]}.</h1>

      <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar border-b border-cream/10 pb-1">
        {TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setQp({ tab: t.k })}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${active === t.k ? "text-gold border-b border-gold" : "text-cream/60 hover:text-cream"}`}
          >{t.l}</button>
        ))}
      </div>

      <div className="mt-8">
        {active === "home" && <Home studio={studio} bookings={bookings} earnings={earnings} />}
        {active === "today" && <Today bookings={bookings} />}
        {active === "calendar" && <Calendar studio={studio} bookings={bookings} />}
        {active === "bookings" && <BookingsTabs bookings={bookings} />}
        {active === "earnings" && <Earnings earnings={earnings} bookings={bookings} />}
        {active === "performance" && <Performance studio={studio} bookings={bookings} earnings={earnings} />}
      </div>
    </div>
  );
}

function fmtTime(iso) { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
function isToday(iso) { const d = new Date(iso), n = new Date(); return d.toDateString() === n.toDateString(); }
function thisWeek(iso) {
  const d = new Date(iso), n = new Date(); const diff = (d - n) / 86400000;
  return diff >= -7 && diff <= 7;
}

function Home({ studio, bookings, earnings }) {
  const todayList = bookings.filter((b) => isToday(b.start_time));
  const upcoming = bookings.filter((b) => new Date(b.start_time) > new Date() && b.status !== "cancelled").sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  const weekEarn = bookings.filter((b) => b.status === "completed" && thisWeek(b.check_out || b.start_time)).reduce((s, b) => s + (b.professional_payout || 0), 0);
  const next = upcoming[0];
  return (
    <div className="space-y-6" data-testid="dash-home">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Users} label="Today" value={`${todayList.length} bookings`} />
        <StatCard icon={ChartLine} label="This week" value={`$${weekEarn.toFixed(0)} earned`} />
        <StatCard icon={Clock} label="Next" value={next ? `${fmtTime(next.start_time)} · ${next.service_name}` : "—"} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Link to="?tab=today" data-testid="cta-today" className="btn-gold px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em] font-semibold">View today</Link>
        <Link to="?tab=calendar" data-testid="cta-availability" className="btn-ghost px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em]">Open availability</Link>
        <Link to={`/studio/${studio.id}`} data-testid="cta-edit-studio" className="btn-ghost px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em]">View my Studio</Link>
        <Link to="/scan-pass" data-testid="cta-scan" className="btn-ghost px-5 py-3 rounded-full text-xs uppercase tracking-[0.25em]">Scan KUTZ Pass</Link>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-cream/10 p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-cream/60"><Icon size={12} color="#C5A059" /> {label}</div>
      <p className="font-display text-2xl mt-2">{value}</p>
    </div>
  );
}

function Today({ bookings }) {
  const items = bookings.filter((b) => isToday(b.start_time)).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  return (
    <div className="space-y-3" data-testid="dash-today">
      {items.length === 0 && <p className="text-cream/60">Nothing scheduled today.</p>}
      {items.map((b) => (
        <div key={b.id} className="border border-cream/10 p-4 flex items-center justify-between" data-testid={`today-${b.id}`}>
          <div>
            <p className="font-display text-lg">{b.member_name} · {b.service_name}</p>
            <p className="text-xs text-cream/60">{fmtTime(b.start_time)} · ${b.price} · <span className="uppercase tracking-[0.2em] text-[10px]">{b.status}</span></p>
          </div>
          <Link to="/scan-pass" className="btn-gold px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1" data-testid={`today-scan-${b.id}`}>
            {b.status === "confirmed" ? "Scan Pass" : b.status === "active" ? "Complete" : "View"} <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      ))}
    </div>
  );
}

function Calendar({ studio, bookings }) {
  const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const now = new Date();
  const week = days.map((d, i) => {
    const dt = new Date(now); dt.setDate(now.getDate() + ((i + 1 - now.getDay() + 7) % 7));
    const list = bookings.filter((b) => new Date(b.start_time).toDateString() === dt.toDateString());
    const openSlots = (studio.availability || []).filter((iso) => new Date(iso).toDateString() === dt.toDateString());
    return { d, dt, list, openSlots };
  });
  return (
    <div className="space-y-3" data-testid="dash-calendar">
      <div className="flex items-center gap-3 text-xs">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gold inline-block" /> Booked</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 border border-cream/40 inline-block" /> Open</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-cream/20 inline-block" /> Blocked</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {week.map(({ d, dt, list, openSlots }) => (
          <div key={d} className="border border-cream/10 p-3 min-h-[120px]" data-testid={`cal-${d}`}>
            <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60">{d} {dt.getDate()}</p>
            {list.map((b) => (
              <div key={b.id} className="mt-2 bg-gold text-ink text-[10px] px-2 py-1 rounded-sm">
                {fmtTime(b.start_time)} {b.member_name.split(" ")[0]}
              </div>
            ))}
            {openSlots.map((iso, i) => (
              <div key={i} className="mt-2 border border-cream/30 text-[10px] px-2 py-1 rounded-sm">
                {fmtTime(iso)} open
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function BookingsTabs({ bookings }) {
  const [f, setF] = useState("upcoming");
  const map = {
    upcoming: bookings.filter((b) => b.status === "confirmed" || b.status === "active"),
    requests: bookings.filter((b) => b.status === "requested"),
    completed: bookings.filter((b) => b.status === "completed"),
    cancelled: bookings.filter((b) => b.status === "cancelled"),
  };
  return (
    <div data-testid="dash-bookings">
      <div className="flex gap-2 mb-5">
        {[["upcoming","Upcoming"],["requests","Requests"],["completed","Completed"],["cancelled","Cancelled"]].map(([k, l]) => (
          <button key={k} onClick={() => setF(k)} data-testid={`bkg-tab-${k}`} className={`chip ${f === k ? "border-gold bg-gold text-ink font-semibold" : ""}`}>{l} ({map[k].length})</button>
        ))}
      </div>
      <div className="space-y-3">
        {map[f].length === 0 && <p className="text-cream/60">Nothing here.</p>}
        {map[f].map((b) => (
          <div key={b.id} className="border border-cream/10 p-4" data-testid={`bkg-${b.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display text-lg">{b.member_name} · {b.service_name}</p>
                <p className="text-xs text-cream/60">{new Date(b.start_time).toLocaleString()} · ${b.price} · <span className="uppercase tracking-[0.2em] text-[10px]">{b.status}</span>{b.is_repeat_discount ? " · repeat" : ""}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Earnings({ earnings, bookings }) {
  const [scope, setScope] = useState("all");
  const [expanded, setExpanded] = useState(null);
  if (!earnings) return <p className="text-cream/60">Loading…</p>;
  const filterByScope = (b) => {
    if (b.status !== "completed") return false;
    const iso = b.check_out || b.start_time;
    if (scope === "all") return true;
    const d = new Date(iso), n = new Date();
    if (scope === "today") return d.toDateString() === n.toDateString();
    if (scope === "week") { const diff = (n - d) / 86400000; return diff >= 0 && diff <= 7; }
    if (scope === "month") return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
    if (scope === "year") return d.getFullYear() === n.getFullYear();
    return true;
  };
  const list = bookings.filter(filterByScope);
  const gross = list.reduce((s, b) => s + b.price, 0);
  const fees = list.reduce((s, b) => s + (b.platform_fee || 0), 0);
  const waivers = list.filter((b) => b.is_repeat_discount).reduce((s, b) => s + (b.price * 0.08), 0);
  const net = list.reduce((s, b) => s + (b.professional_payout || 0), 0);
  return (
    <div className="space-y-5" data-testid="dash-earnings">
      <div className="flex flex-wrap gap-2">
        {[["today","Today"],["week","This week"],["month","This month"],["year","This year"],["all","All time"]].map(([k, l]) => (
          <button key={k} data-testid={`earn-${k}`} onClick={() => setScope(k)} className={`chip ${scope === k ? "border-gold bg-gold text-ink font-semibold" : ""}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="earnings-summary">
        <StatCard icon={ChartLine} label="Service value" value={`$${gross.toFixed(2)}`} />
        <StatCard icon={ChartLine} label="KUTZ fees" value={`-$${fees.toFixed(2)}`} />
        <StatCard icon={Sparkle} label="Fee waivers" value={`$${waivers.toFixed(2)}`} />
        <StatCard icon={ChartLine} label="Your earnings" value={`$${net.toFixed(2)}`} />
      </div>
      <div className="border border-gold/40 bg-gold/5 p-5" data-testid="income-created">
        <p className="overline">Income you've created through KUTZ</p>
        <p className="font-display text-4xl text-gold mt-2">${(earnings.net || 0).toFixed(2)}</p>
      </div>
      <div className="space-y-2">
        {list.map((b) => (
          <div key={b.id} className="border border-cream/10" data-testid={`earn-row-${b.id}`}>
            <button onClick={() => setExpanded(expanded === b.id ? null : b.id)} className="w-full text-left p-4 flex items-center justify-between">
              <div>
                <p className="font-display text-lg">{b.service_name}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-cream/60">{new Date(b.check_out || b.start_time).toLocaleDateString()} · {b.member_name}</p>
              </div>
              <p className="font-display text-xl text-gold">${(b.professional_payout ?? 0).toFixed(2)}</p>
            </button>
            {expanded === b.id && (
              <div className="border-t border-cream/10 p-4 text-sm space-y-1" data-testid={`earn-detail-${b.id}`}>
                <div className="flex justify-between"><span>{b.service_name}</span><span>${b.price.toFixed(2)}</span></div>
                {b.is_repeat_discount ? (
                  <>
                    <div className="flex justify-between text-gold"><span>KUTZ loyalty fee</span><span>$0.00</span></div>
                    <div className="flex justify-between font-display"><span>Professional earnings</span><span>${b.price.toFixed(2)}</span></div>
                    <div className="mt-3 border border-gold/40 bg-gold/5 p-3 text-xs" data-testid={`waiver-banner-${b.id}`}>
                      {b.member_name.toUpperCase().split(" ")[0]} BOOKED YOU AGAIN. KUTZ WAIVED YOUR PLATFORM FEE.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between"><span>KUTZ fee 8%</span><span>-${(b.platform_fee || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between font-display"><span>Professional earnings</span><span>${(b.professional_payout || 0).toFixed(2)}</span></div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Performance({ studio, bookings, earnings }) {
  const completed = bookings.filter((b) => b.status === "completed");
  const repeats = completed.filter((b) => b.is_repeat_discount).length;
  const repeatRate = completed.length ? Math.round((repeats / completed.length) * 100) : 0;
  const svcCounts = {};
  completed.forEach((b) => { svcCounts[b.service_name] = (svcCounts[b.service_name] || 0) + 1; });
  const mostBooked = Object.entries(svcCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="dash-performance">
      <StatCard icon={Star} label="Rating" value={`${studio.rating} ★`} />
      <StatCard icon={Users} label="Completed cuts" value={`${studio.completed_cuts}`} />
      <StatCard icon={Sparkle} label="Repeat rate" value={`${repeatRate}%`} />
      <StatCard icon={ChartLine} label="Booking completion" value="98%" />
      <StatCard icon={Storefront} label="Most booked" value={mostBooked} />
      <StatCard icon={Clock} label="Busiest" value="Thursday 6–9 PM" />
      <div className="col-span-2 md:col-span-3 border border-cream/10 p-5" data-testid="milestone">
        <p className="overline mb-2">Milestone</p>
        <p className="font-display text-2xl">{studio.completed_cuts} / 250 cuts</p>
        <div className="mt-3 h-1 bg-cream/10"><div className="h-1 bg-gold" style={{ width: `${Math.min(100, (studio.completed_cuts / 250) * 100)}%` }} /></div>
      </div>
    </div>
  );
}
